import React, { useEffect, useState } from 'react'

import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'

import './contatcts.css'

function resolveContractsApiUrl(pathOrUrl: string): string {
  const raw = pathOrUrl.trim()
  if (!raw) return ''
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw
  }
  const baseRaw = process.env.REACT_APP_API_BASE_URL?.trim().replace(/\/$/, '')
  if (
    baseRaw &&
    (baseRaw.startsWith('http://') || baseRaw.startsWith('https://'))
  ) {
    const path = raw.startsWith('/') ? raw : `/${raw}`
    return `${baseRaw}${path}`
  }
  return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`
}

/** STP listing for Contract STP System table (`REACT_APP_API_STP`). */
function buildStpContractsListUrl(): string {
  const trimmed = process.env.REACT_APP_API_STP?.trim()
  const pathOrUrl = trimmed || '/api/stp'
  return resolveContractsApiUrl(pathOrUrl)
}

function extractContractsList(json: unknown): Record<string, unknown>[] {
  if (Array.isArray(json)) {
    return json.filter(
      (x): x is Record<string, unknown> =>
        x !== null && typeof x === 'object' && !Array.isArray(x)
    )
  }
  if (!json || typeof json !== 'object' || Array.isArray(json)) return []
  const o = json as Record<string, unknown>
  for (const k of ['data', 'stp', 'contracts', 'records', 'items', 'results']) {
    const v = o[k]
    if (Array.isArray(v)) {
      return v.filter(
        (x): x is Record<string, unknown> =>
          x !== null && typeof x === 'object' && !Array.isArray(x)
      )
    }
  }
  return []
}

const CONTRACT_TABLE_TRUNCATE_LEN = 8

function ContractsTruncatedText({ text }: { text: string }) {
  if (
    !text ||
    text === '—' ||
    text.length <= CONTRACT_TABLE_TRUNCATE_LEN
  ) {
    return <span>{text}</span>
  }

  const preview = `${text.slice(0, CONTRACT_TABLE_TRUNCATE_LEN)}...`

  return (
    <span title={text} className="contatcts-contracts-ellipsis-cell">
      {preview}
    </span>
  )
}

const Contatcts = () => {
  const history = useHistory()
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  type ContractRow = {
    id: string
    vendorName: string
    contractNumber: string
    contractName: string
    contractDateStart: string
    contractDateEnd: string
    location: string
    isActive: string
    createdAt: string
  }

  const [rows, setRows] = useState<ContractRow[]>([])

  const toCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—'
    const text = String(value).trim()
    return text ? text : '—'
  }

  const toDateCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—'
    const text = String(value).trim()
    if (!text) return '—'

    // Keep date inputs and API timestamps consistent as yyyy-mm-dd.
    const simpleDateMatch = text.match(/^(\d{4}-\d{2}-\d{2})/)
    if (simpleDateMatch) {
      return simpleDateMatch[1]
    }

    const parsed = new Date(text)
    if (Number.isNaN(parsed.getTime())) {
      return text
    }

    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const unwrapActiveValue = (value: unknown): unknown => {
    let v: unknown = value
    for (let depth = 0; depth < 4; depth += 1) {
      if (v === null || v === undefined) return v
      if (typeof v !== 'object' || Array.isArray(v)) return v
      const o = v as Record<string, unknown>
      const next =
        o.value ??
        o.Value ??
        o.data ??
        o.Data ??
        o.isActive ??
        o.IsActive ??
        o.active ??
        o.Active ??
        o.state ??
        o.enabled
      if (next === undefined) return v
      v = next
    }
    return v
  }

  /** Mongo / extended JSON style wrappers (`{ "$numberInt": "1" }`). */
  const normalizeActivePrimitive = (value: unknown): unknown => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return value
    }
    const o = value as Record<string, unknown>
    if ('$numberInt' in o) return Number(o.$numberInt)
    if ('$numberLong' in o) return Number(o.$numberLong)
    if ('$numberDouble' in o) return Number(o.$numberDouble)
    return value
  }

  /** `1` / `"1"` → Yes; any other primitive (including `0`) → No. Missing stays —. */
  const toIsActiveDisplay = (value: unknown): string => {
    let v: unknown = unwrapActiveValue(value)
    while (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      const inner = normalizeActivePrimitive(v)
      if (inner === v) break
      v = inner
    }
    v = normalizeActivePrimitive(v)

    if (v === null || v === undefined) return '—'

    if (typeof v === 'boolean') return v ? 'Yes' : 'No'

    if (typeof v === 'number') {
      if (Number.isNaN(v)) return '—'
      return v === 1 ? 'Yes' : 'No'
    }

    if (typeof v === 'object') return '—'

    const t = String(v).trim()
    if (!t) return '—'

    const n = Number(t)
    if (!Number.isNaN(n) && Number.isFinite(n) && t === String(n).trim()) {
      return n === 1 ? 'Yes' : 'No'
    }
    if (t === '1') return 'Yes'

    const lower = t.toLowerCase()
    if (lower === 'true' || lower === 'yes' || lower === 'y') return 'Yes'
    return 'No'
  }

  const mapApiRowToContractRow = (
    item: Record<string, unknown>,
    index: number
  ): ContractRow => {
    const rawId =
      item.Id ??
      item.id ??
      item._id ??
      item.stp_id ??
      item.stpId ??
      item.contract_id ??
      item.stp_contract_number ??
      item.stpContractNumber ??
      item.contract_number ??
      item.contractNumber ??
      item.plantId ??
      `row-${index}`

    return {
      id: String(rawId),
      vendorName: toCellValue(
        item.VendorName ??
          item.vendorName ??
          item.stp_vendor_name ??
          item.stpVendorName ??
          item.vendor_name ??
          item.stp_vendor ??
          item.stpVendor
      ),
      contractNumber: toCellValue(
        item.ContractNumber ??
          item.contractNumber ??
          item.stp_contract_number ??
          item.stpContractNumber ??
          item.contract_number ??
          item.contract_no ??
          item.contractNo
      ),
      contractName: toCellValue(
        item.ContractName ??
          item.contractName ??
          item.stp_contract_name ??
          item.stpContractName ??
          item.contract_name
      ),
      contractDateStart: toDateCellValue(
        item.ContractDateStart ??
          item.contractDateStart ??
          item.stp_contract_date_start ??
          item.stpContractDateStart ??
          item.contract_start ??
          item.contractStart ??
          item.contract_date_start ??
          item.contractDateStart ??
          item.start_date ??
          item.startDate
      ),
      contractDateEnd: toDateCellValue(
        item.ContractDateEnd ??
          item.contractDateEnd ??
          item.stp_contract_date_end ??
          item.stpContractDateEnd ??
          item.contract_end ??
          item.contractEnd ??
          item.contract_date_end ??
          item.contractDateEnd ??
          item.end_date ??
          item.endDate
      ),
      location: toCellValue(
        item.Location ??
          item.location ??
          item.stp_location ??
          item.stpLocation ??
          item.city_state ??
          item.cityState ??
          item.address
      ),
      isActive: toIsActiveDisplay(
        item.IsActive ??
          item.isActive ??
          item.IS_ACTIVE ??
          item.is_active ??
          item.STP_Active ??
          item.stp_active ??
          item.stpIsActive ??
          item.stp_is_active ??
          item.active ??
          item.Active ??
          item.enabled ??
          item.Enabled ??
          item.status ??
          item.Status
      ),
      createdAt: toDateCellValue(
        item.CreatedAt ??
          item.createdAt ??
          item.created_at ??
          item.date_created ??
          item.dateCreated
      ),
    }
  }

  useEffect(() => {
    const fetchContracts = async () => {
      const url = buildStpContractsListUrl()

      setListLoading(true)
      setListError(null)

      try {
        const res = await fetch(url, { method: 'GET' })
        if (!res.ok) {
          throw new Error(`Failed to fetch contracts (${res.status})`)
        }

        const data: unknown = await res.json()
        const list = extractContractsList(data)
        const mappedRows = list.map((item, idx) =>
          mapApiRowToContractRow(item, idx)
        )
        setRows(mappedRows)
      } catch (e) {
        setListError(
          e instanceof Error ? e.message : 'Failed to fetch contracts.'
        )
      } finally {
        setListLoading(false)
      }
    }

    fetchContracts()
  }, [])

  const goToPlants = () => {
    history.push('/plants')
  }

  const goToPenalty = () => {
    history.push('/penalty')
  }

  const goToContracts = () => {
    history.push('/contatcts')
  }

  const goToDashboard = () => {
    history.push('/dashboard')
  }

  const goToReports = () => {
    history.push('/reports')
  }

  const goToMonitoring = () => {
    history.push('/monitoring')
  }

  const goToCctv = () => {
    history.push('/cctv')
  }

  const goToInventory = () => {
    history.push('/inventory')
  }

  const goToSettings = () => {
    history.push('/settings')
  }

  const handleSignOut = () => {
    history.push('/')
  }

  return (
    <div className="contatcts-container1">
      <Helmet>
        <title>Contatcts - exported project</title>
        <meta property="og:title" content="Contatcts - exported project" />
        <link
          rel="canonical"
          href="https://namami-ganga-design-file-pvya6a.teleporthq.app/contatcts"
        />
        <meta
          property="og:url"
          content="https://namami-ganga-design-file-pvya6a.teleporthq.app/contatcts"
        />
      </Helmet>
      <div className="contatcts-thq-contatcts-elm">
        <span className="contatcts-thq-text-elm10">Dashboard</span>
        <img
          src="/bg1106-74is-300w.png"
          alt="BG1106"
          className="contatcts-thq-bg-elm"
        />
        <div className="contatcts-thq-settings-elm1" onClick={goToPlants}>
          <span className="contatcts-thq-text-elm11">Plants</span>
        </div>
        <div className="contatcts-thq-settings-elm2" onClick={goToPenalty}>
          <span className="contatcts-thq-text-elm12">Penalty</span>
        </div>
        <div className="contatcts-thq-settings-elm3" onClick={goToReports}>
          <span className="contatcts-thq-text-elm13">Reports</span>
        </div>
        <div className="contatcts-thq-settings-elm4" onClick={goToMonitoring}>
          <span className="contatcts-thq-text-elm14">Real time monitoring</span>
        </div>
        <div className="contatcts-thq-settings-elm5">
          <span className="contatcts-thq-text-elm15">Manpower</span>
        </div>
        <div className="contatcts-thq-settings-elm6" onClick={goToInventory}>
          <span className="contatcts-thq-text-elm16">Inventory</span>
        </div>
        <span className="contatcts-thq-text-elm17">Collaboration</span>
        <span className="contatcts-thq-text-elm18" onClick={goToCctv} role="presentation">
          CCTV Monitoring
        </span>
        <span className="contatcts-thq-text-elm19" onClick={goToSettings} role="presentation">
          Settings
        </span>
        <div
          className="contatcts-thq-dashboard-elm"
          onClick={goToDashboard}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') goToDashboard()
          }}
          role="button"
          tabIndex={0}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
          <span className="contatcts-thq-text-elm20">Dashboard</span>
          <img
            src="/rectangle2891108-6h0r-200h.png"
            alt="Rectangle2891108"
            className="contatcts-thq-rectangle289-elm"
          />
        </div>
        <img
          src="/grid1108-bcys.svg"
          alt="Grid1108"
          className="contatcts-thq-grid-elm"
        />
        <img
          src="/settings1108-nqpq.svg"
          alt="Settings1108"
          className="contatcts-thq-settings-elm7"
        />
        <img
          src="/ellipse37041108-v5wb-200h.png"
          alt="Ellipse37041108"
          className="contatcts-thq-ellipse3704-elm"
        />
        <img
          src="/ellipse37051108-l57m-200h.png"
          alt="Ellipse37051108"
          className="contatcts-thq-ellipse3705-elm"
        />
        <img
          src="/line4771108-7q3.svg"
          alt="Line4771108"
          className="contatcts-thq-line477-elm"
        />
        <img
          src="/line4781108-mzct.svg"
          alt="Line4781108"
          className="contatcts-thq-line478-elm"
        />
        <img
          src="/chevron1108-go44.svg"
          alt="Chevron1108"
          className="contatcts-thq-chevron-elm"
        />
        <img
          src="/image1108-dphs-200h.png"
          alt="Image1108"
          className="contatcts-thq-image-elm"
        />
        <span className="contatcts-thq-text-elm21">Rajesh Nair</span>
        <span className="contatcts-thq-text-elm22">Super Admin</span>
        <img
          src="/arrowexit1109-faef.svg"
          alt="Sign out"
          className="contatcts-thq-arrow-exit-elm"
          onClick={handleSignOut}
        />
        <img
          src="/alert1109-5a5o.svg"
          alt="Alert1109"
          className="contatcts-thq-alert-elm"
        />
        <div className="contatcts-thq-frame99-elm">
          <div className="contatcts-thq-frame101-elm1">
            <span className="contatcts-thq-text-elm23">Active Sites</span>
          </div>
          <span className="contatcts-thq-text-elm24">34</span>
        </div>
        <div className="contatcts-thq-frame100-elm">
          <div className="contatcts-thq-frame101-elm2">
            <span className="contatcts-thq-text-elm25">Green Plants</span>
          </div>
          <span className="contatcts-thq-text-elm26">32</span>
        </div>
        <div className="contatcts-thq-frame102-elm">
          <div className="contatcts-thq-frame101-elm3">
            <span className="contatcts-thq-text-elm27">Critical Alerts</span>
          </div>
        </div>
        <div className="contatcts-thq-frame1984080068-elm1">
          <div className="contatcts-thq-frame101-elm4">
            <span className="contatcts-thq-text-elm28">Total Penalties</span>
          </div>
          <span className="contatcts-thq-text-elm29">₹50,000</span>
          <img
            src="/rectangle1301111-ib15-200h.png"
            alt="Rectangle1301111"
            className="contatcts-thq-rectangle130-elm"
          />
          <span className="contatcts-thq-text-elm30">8 violations</span>
        </div>
        <img
          src="/namamigangelogohindi11111-5u0o-200h.png"
          alt="NamamiGangeLogoHindi11111"
          className="contatcts-thq-namami-gange-logo-hindi1-elm"
        />
        <span className="contatcts-thq-text-elm31" onClick={goToContracts}>
          Contracts
        </span>
        <div className="contatcts-thq-frame1984080070-elm">
          <span className="contatcts-thq-text-elm52">Contract STP System</span>
          <div className="contatcts-contracts-wrap">
            {listError && (
              <div className="contatcts-contract-error">{listError}</div>
            )}
            {listLoading && (
              <div className="contatcts-contract-loading">Loading contracts...</div>
            )}
            <div
              className="contatcts-contracts-scroll-region"
              role="region"
              aria-label="Contract STP table"
            >
              <div className="contatcts-contracts-scroll-inner">
                <div className="contatcts-contracts-grid contatcts-contracts-head">
                  <div>Id</div>
                  <div>VendorName</div>
                  <div>ContractNumber</div>
                  <div>ContractName</div>
                  <div>ContractDateStart</div>
                  <div>ContractDateEnd</div>
                  <div>Location</div>
                  <div>IsActive</div>
                  <div>CreatedAt</div>
                </div>

                {rows.map((row, idx) => (
                  <div
                    key={`${row.id}-${idx}`}
                    className="contatcts-contracts-grid contatcts-contracts-row"
                  >
                    <div>
                      <ContractsTruncatedText text={row.id} />
                    </div>
                    <div>
                      <ContractsTruncatedText text={row.vendorName} />
                    </div>
                    <div>{row.contractNumber}</div>
                    <div>
                      <ContractsTruncatedText text={row.contractName} />
                    </div>
                    <div>{row.contractDateStart}</div>
                    <div>{row.contractDateEnd}</div>
                    <div>
                      <ContractsTruncatedText text={row.location} />
                    </div>
                    <div>{row.isActive}</div>
                    <div>{row.createdAt}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contatcts

